import { useCallback, useEffect, useRef } from 'react';

/**
 * useDebouncedCallback — coalesce a burst of calls into a single trailing invocation,
 * with a max-wait so a *sustained* stream still fires at least every `maxWait` (#1309).
 *
 * Lifted from the proven #614 pattern (`pages/user/ObservationDetail.js`) that collapses
 * rapid WebSocket events into one API call. Used to stop `ObservationList` and
 * `TimelineView` from re-asking the scheduler on every `observation_log_entry`.
 *
 * Implementation discipline (react-hooks/exhaustive-deps is OFF in this package, so this
 * is the only guardrail):
 *  - **ref-to-latest callback** — always invokes the newest `callback` without the returned
 *    function's identity changing (no stale closure, no re-subscribe churn in WS effects);
 *  - **referentially stable** returned function (`useCallback([])`), safe to put in effect deps;
 *  - **unmount-only cleanup** — the pending timer is cleared once, on unmount (a per-render
 *    cleanup would perpetually cancel the trailing fire).
 *
 * @param {Function} callback  invoked (with the latest identity) on the trailing edge
 * @param {{delay?:number, maxWait?:number}} [opts]
 * @returns {[Function, Function]} [debounced, cancel]
 */
export function useDebouncedCallback(callback, opts = {}) {
  const { delay = 2000, maxWait = 5000 } = opts;

  const cbRef = useRef(callback);
  cbRef.current = callback; // latest, every render
  const delayRef = useRef(delay);
  delayRef.current = delay;
  const maxWaitRef = useRef(maxWait);
  maxWaitRef.current = maxWait;

  const timerRef = useRef(null);
  const firstScheduledRef = useRef(null);

  const fire = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    firstScheduledRef.current = null;
    cbRef.current();
  }, []);

  const debounced = useCallback(() => {
    const now = Date.now();
    if (firstScheduledRef.current === null) firstScheduledRef.current = now;
    const waited = now - firstScheduledRef.current;

    if (waited >= maxWaitRef.current) {
      fire(); // sustained stream — fire now, reset the window
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(fire, delayRef.current);
  }, [fire]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    firstScheduledRef.current = null;
  }, []);

  // Clear the pending timer only on unmount.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return [debounced, cancel];
}
